// What a 409 looks like to the person who hit it.
//
// §06 gives every mutable row a `version` and rejects a PATCH whose `If-Match`
// doesn't match it. That 409 is not "an error occurred" — it is "somebody saved
// this row after you opened it", which is a situation with exactly two honest
// resolutions and no correct default. A CRM where two people edit a deal and
// one silently wins is worse than one that refuses.
//
// So: nothing is discarded, nothing is retried behind the user's back, and the
// choice is only offered AFTER they can see what the other change actually was.

import type { ReactNode } from "react";
import styles from "./forms.module.css";
import { Button } from "../design/Button.js";
import { Notice, ProblemMessage, Spinner } from "../design/Feedback.js";
import { Table, Td, Th } from "../design/Table.js";
import { assertNever } from "../lib/assertNever.js";
import type { ConflictState, WireValue } from "./useEditor.js";

export type ValueFormatter = (field: string, value: WireValue) => string;

const defaultFormat: ValueFormatter = (_field, value) => {
  if (value === null || value === undefined) return "—";
  if (value === "") return "(empty)";
  // A clause is an object, and `String(…)` of one is "[object Object]" — which
  // in a column headed "Yours" against a column headed "Stored now" is two
  // values a reader cannot tell apart.
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

export interface ConflictPanelProps<TRecord> {
  conflict: ConflictState<TRecord>;
  /** Field name -> the label this form shows for it. */
  labels: Readonly<Record<string, string>>;
  /** For money and percentages, so the comparison isn't a wall of minor units. */
  formatValue?: ValueFormatter;
  onReload: () => void;
  onKeepMine: () => void;
  onUseTheirs: () => void;
}

export function ConflictPanel<TRecord>({
  conflict,
  labels,
  formatValue = defaultFormat,
  onReload,
  onKeepMine,
  onUseTheirs,
}: ConflictPanelProps<TRecord>): ReactNode {
  switch (conflict.phase) {
    case "detected":
      return (
        <Notice
          tone="warn"
          title="Someone else changed this while you had it open."
          actions={
            <Button small variant="primary" onClick={onReload}>
              Show me what changed
            </Button>
          }
        >
          Your copy is from before their change, so it was not saved — nothing you typed has been
          lost. Load what is stored now and decide which version wins.
          <span className={styles.context}>{` ${conflict.problem.title}`}</span>
        </Notice>
      );

    case "reloading":
      return <Spinner label="Loading the current values…" inline />;

    case "reload_failed":
      return (
        <ProblemMessage
          error={conflict.error}
          action="load the current values"
          onRetry={onReload}
        />
      );

    case "compared":
      return (
        <div className={styles.conflict}>
          <Notice tone="warn" title="This record moved on while you had it open.">
            {conflict.differences.length === 0
              ? "Nothing you edited is affected — the change was to a field this form doesn't touch. Saving now applies your edit on top of it."
              : "Your version is still in the form. Choose which one to keep."}
          </Notice>

          {conflict.differences.length > 0 && (
            <Table label="Your values compared with the stored ones">
              <thead>
                <tr>
                  <Th>Field</Th>
                  <Th>Yours</Th>
                  <Th>Stored now</Th>
                </tr>
              </thead>
              <tbody>
                {conflict.differences.map((difference) => (
                  <tr key={difference.field}>
                    <Td>
                      {labels[difference.field] ?? difference.field}
                      {!difference.editedByYou && (
                        // Not a clash: they changed something this form loaded
                        // but the user never touched. Worth seeing, not worth
                        // making them choose about.
                        <span className={styles.context}> · you didn&apos;t change this</span>
                      )}
                    </Td>
                    <Td className={difference.editedByYou ? styles.mine : styles.context}>
                      {formatValue(difference.field, difference.mine)}
                    </Td>
                    <Td className={styles.theirs}>
                      {formatValue(difference.field, difference.theirs)}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          <div className={styles.conflictActions}>
            <Button variant="primary" onClick={onKeepMine}>
              Keep my values
            </Button>
            <Button onClick={onUseTheirs}>Use the stored values</Button>
          </div>
          <p className={styles.conflictNote}>
            “Keep my values” saves your version on top of theirs, at the version just loaded. “Use
            the stored values” replaces what is in the form — the only thing in this flow that
            discards your typing, and only because you asked.
          </p>
        </div>
      );

    default:
      return assertNever(conflict, "conflict phase");
  }
}
